import { Dialog, DialogFooter, DialogType } from "@fluentui/react/lib/Dialog";
import { PrimaryButton, DefaultButton } from "@fluentui/react/lib/Button";
import { MessageBar, MessageBarType } from "@fluentui/react";
import { TextField } from "@fluentui/react/lib/TextField";
import { useState, useCallback, useEffect, useRef } from "react";
import CustomDatePicker from "../../Modules/CustomDatePicker/CustomDatePicker";
import { formatDateToMMDDYYYY } from "../../Utilities/CommonFunction";
import { Dropdown } from "@fluentui/react/lib/Dropdown";
import "./ExceptionReportDialog.css";

const BUSINESS_JUSTIFICATION_ERROR_MESSAGE =
    "Business justification cannot be empty!";
const defaultExpirationDate = () => {
    const time = new Date();
    time.setTime(time.getTime() + 24*60*60*1000*180);
    return time;
};
const modelProps = {
    isBlocking: true,
    topOffsetFixed: true,
    className: "exception_report_dialog",
};
const dropdownStyles = { dropdown: { minWidth: 100 } };

interface IExceptionReportDialogProps {
    showReportExceptionDialog: boolean,
    dismissReportDialog: Function,
    onSubmit: Function,
    isLoading?: boolean,
    reportType?: string
}

export default function ExceptionReportDialog(
    props: IExceptionReportDialogProps
) {
    const [businessJustificationValue, setBusinessJustificationValue] = useState<string>("");
    const [
        businessJustificationErrorMessage,
        setBusinessJustificationErrorMessage,
    ] = useState<string>("");
    const [expirationDate, setExpirationDate] = useState<string>(
        formatDateToMMDDYYYY(defaultExpirationDate())
    );
    const [exceptionType, setExceptionType] = useState<string>("");
    const [dropdownError, setDropdownError] = useState<boolean>(false);
    const firstRenderFlag = useRef(0)

    useEffect(() => {
        if (!businessJustificationValue && firstRenderFlag.current > 0) {
            setBusinessJustificationErrorMessage(
                BUSINESS_JUSTIFICATION_ERROR_MESSAGE
            );
        } else {
            setBusinessJustificationErrorMessage("");
        }
        firstRenderFlag.current ++
    }, [businessJustificationValue]);

    const resetExemptDialogData = useCallback(function (date) {
        setExpirationDate(date);
    }, []);

    const validateInputForFalsePositive = () => {
        if (!businessJustificationValue) {
            setBusinessJustificationErrorMessage(
                BUSINESS_JUSTIFICATION_ERROR_MESSAGE
            );
            return false;
        }
        return true;
    }

    const validateInputForException = () => {
        if (!exceptionType) {
            setDropdownError(true);
        }else {
            setDropdownError(false);
        }
        if (!businessJustificationValue) {
            setBusinessJustificationErrorMessage(
                BUSINESS_JUSTIFICATION_ERROR_MESSAGE
            );
        }
        if (!businessJustificationValue || !exceptionType || !expirationDate) {
            return false;
        }
        return true;
    };

    const dismissDialog = () => {
        setDropdownError(false);
        setBusinessJustificationErrorMessage('');
        props.dismissReportDialog();
    };

    const submitDialog = () => {
        if(props.reportType === 'exception' && validateInputForException()) {
            props.onSubmit(exceptionType,businessJustificationValue,expirationDate)
        }
        if(props.reportType === 'falsePositive' && validateInputForFalsePositive()) {
            props.onSubmit('False Positive',businessJustificationValue,formatDateToMMDDYYYY(defaultExpirationDate()))
        }
    };

    function onTypeChange(ev: React.FormEvent<HTMLInputElement>, option: any) {
        setExceptionType(option.text);
    }

    const renderExceptionContent = () => {
        return (
            <>
                <Dropdown
                    placeholder="Select a type of exception"
                    label="Select a type of exception"
                    options={[
                        { key: "Exception", text: "Exception" },
                        // { key: "By Design", text: "By Design" },
                        // { key: "Others", text: "Others" },
                    ]}
                    required={true}
                    onChange={onTypeChange}
                    styles={dropdownStyles}
                />
                {dropdownError && (
                    <p className="exception_report_dialog_errorMessage">
                        <span data-automation-id="error-message">
                            Exception type cannot be empty!
                        </span>
                    </p>
                )}
                <TextField
                    label="Business justification:"
                    required={true}
                    placeholder="Please provide the business justification"
                    errorMessage={businessJustificationErrorMessage}
                    rows={4}
                    multiline
                    resizable={false}
                    onChange={(e, newValue) =>
                        setBusinessJustificationValue(newValue)
                    }
                />
                <CustomDatePicker
                    label="Expiration Date:"
                    required={true}
                    defaultDate={defaultExpirationDate()}
                    getDate={resetExemptDialogData}
                />
            </>
        )
    }

    const renderFalsePositiveContent = () => {
        return (
            <>
                <MessageBar
                    messageBarType={MessageBarType.warning}
                    className="exception_report_warning"
                >
                    Do you want to report this as a false case to the Content Privacy Concern Review team (SecureCI@microsoft.com) in order to assist with the research?
                </MessageBar>
                <TextField
                    label="Business justification:"
                    required={true}
                    placeholder="Please provide the business justification"
                    errorMessage={businessJustificationErrorMessage}
                    rows={4}
                    multiline
                    resizable={false}
                    onChange={(e, newValue) =>
                        setBusinessJustificationValue(newValue)
                    }
                />
            </>
        )
    }

    return (
        <>
            <h3>{props.reportType === 'exception'? 'Add/Renew Exceptions': 'Report False Positive'}</h3>
            {props.reportType === 'exception'? renderExceptionContent(): renderFalsePositiveContent()}
            <DialogFooter>
                <DefaultButton
                    onClick={() => {
                        dismissDialog();
                    }}
                    text="Cancel"
                />
                <PrimaryButton
                    onClick={() => {
                        submitDialog();
                    }}
                    text="Submit"
                />
            </DialogFooter>
        </>
    );
}
